import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { getIncentiveCardsExportAndPopUp } from "../../../store/actions";
import { FormatDate, FormatDateMMdd } from "../../../utils/dateFormat";
import CompanyGUIDs from "../../../utils/GUIDConst";
import { CompanyContext } from "../../../utils/CompanyContext";

const normalize = (val = "") =>
  val.toString().toLowerCase().replaceAll(" ", "");

const SimpleDisclaimer = ({ text }) => (
  <div className="col-md-12">
    <h3
      className="dis"
      style={{ fontSize: "14px", marginTop: "20px", marginBottom: "20px" }}
    >
      <span className="disclaim">*Disclaimer*</span>
      {text}
    </h3>
  </div>
);

const getDisclaimerMessage = (type, dashboardType) => {
  const baseMessage = (
    <div className="col-md-12">
      <h3
        className="dis"
        style={{ fontSize: "14px", marginTop: "20px", marginBottom: "20px" }}
      >
        <span className="disclaim">Disclaimer:</span>
        The displayed number is an estimation only and does not reflect some
        factors that impact your bonus
        {(type === "acquisition" || type === "resale") &&
          dashboardType === "L1" &&
          " (such as pilot activity or CSAT count beneath the required minimum)."}
        {(type === "acquisition" || type === "resale") &&
          dashboardType !== "L1" &&
          " (such as leave of absence or your team members flexing between acquisition and resale roles)."}
        Your finalized bonus number will be calculated and shared at the end of
        the quarter. Please contact your manager if you have any questions about
        what is shown on this tile.
        <br />
        Note that{" "}
        {dashboardType === "L1"
          ? "your bonus number will appear blank if you have not yet received a CSAT score in the quarter. Once you've received at least one CSAT score in the quarter, your estimated bonus will be displayed."
          : "if your team includes EPs working across both acquisition and resale, your estimated bonus is the sum of what is shown on this tile between both programs."}
      </h3>
    </div>
  );

  return baseMessage;
};

const getUnitedHealthDisclaimerMessage = () => (
  <div className="col-md-12">
    <h3
      className="dis"
      style={{ fontSize: "14px", marginTop: "20px", marginBottom: "20px" }}
    >
      <span className="disclaim">*Disclaimer*</span>
      Values in the table are subject to change based off system updates,
      operational directions and requirements. To qualify for payout Attendance
      must be at 92.00 or higher.
    </h3>
  </div>
);
const DisclaimerComponent = ({
  currentCompanyGuid,
  primaryProgram,
  dashboard,
  organization,
}) => {
  const program = normalize(primaryProgram);
  const org = normalize(organization);

  /* ================= OPENDOOR ================= */
  if (currentCompanyGuid === CompanyGUIDs["Opendoor"]) {
    if (program.includes("acquisition")) {
      return getDisclaimerMessage("acquisition", dashboard);
    }
    if (program.includes("resale")) {
      return getDisclaimerMessage("resale", dashboard);
    }
  } else if (currentCompanyGuid === CompanyGUIDs["Omnicare365"]) {
    /* ================= OMNICARE ================= */
    if (program === "unitedhealthgroup") {
      return getUnitedHealthDisclaimerMessage();
    }
  } else if (currentCompanyGuid === CompanyGUIDs["Teleperformance"]) {
    /* ================= TELEPERFORMANCE ================= */
    if (
      org.includes("amtrak") &&
      (program.includes("esd") ||
        program.includes("socialmedia") ||
        program.includes("public-spanish") ||
        program.includes("agr") ||
        program.includes("activitymetrics"))
    ) {
      return (
        <SimpleDisclaimer text="Values on the table are subject to change based off system updates, operations directions and requirements. To qualify for payout Shift Adherence must be at 93 or higher with no more than 1 call-off for the month." />
      );
    }

    if (
      org === "extendedstayamerica" &&
      (program === "usgroups" ||
        program === "ussales" ||
        program === "usoutbound")
    ) {
      return (
        <SimpleDisclaimer text="Values on the table are subject to change based off system updates, operations directions and requirements." />
      );
    }

    return (
      <SimpleDisclaimer text="Values on the table are subject to change based off system updates, operations directions and requirements. To qualify for payout Shift Adherence must be at 92 or higher." />
    );
  } else if (currentCompanyGuid === CompanyGUIDs["Movate"]) {
    /* ================= MOVATE ================= */
    if (program === "bmc" || program === "mobilityloyalty") {
      return (
        <SimpleDisclaimer text="Values in the table are subject to change based off system updates, operational directions and requirements." />
      );
    }
  }

  return null;
};

const AgentScheduleModal = ({
  clickedRow,
  showModal,
  toggleModal,
  dashboard,
}) => {
  const dispatch = useDispatch();
  const { currentCompanyGuid } = useContext(CompanyContext);
  const { activeOrganizationInfo, agentInsentiveInfo, isFetching } =
    useSelector(
      ({
        get_active_organization_detail: { data: activeOrganizationData },
        get_incentive_cards_export_and_popup: {
          data: agentInsentiveData,
          isFetching,
        },
      }) => {
        return {
          activeOrganizationInfo: activeOrganizationData || {},
          agentInsentiveInfo: agentInsentiveData,
          isFetching: isFetching,
        };
      },
      shallowEqual,
    );
  const closeBtn = (
    <div
      className="links_right"
      style={{ position: "absolute", top: "15px", right: "15px" }}
    >
      <button
        className="modal_close bg-transparent"
        onClick={toggleModal}
        type="button"
      >
        <img src="/images/cross.svg" />
      </button>
    </div>
  );

  useEffect(() => {
    dispatch(
      getIncentiveCardsExportAndPopUp({
        companyguid: activeOrganizationInfo?.Companyguid,
        organization: activeOrganizationInfo?.Organization,
        program: activeOrganizationInfo?.Program,
        employeenumber: activeOrganizationInfo?.Employeeid,
        dashboard: dashboard || "L1",
        type: "popup",
        startDate: FormatDateMMdd(new Date()),
        endDate: FormatDateMMdd(new Date()),
      }),
    );
  }, [dispatch]);

  const headers =
    agentInsentiveInfo?.length > 0 ? Object.keys(agentInsentiveInfo[0]) : [];
  return (
    <Modal
      isOpen={showModal}
      toggle={toggleModal}
      backdrop={true}
      keyboard={true}
      id="agent_insentive_monthly_modal"
      external={closeBtn}
    >
      <ModalHeader
        // toggle={toggleModal}
        // close={closeBtn}
        className="custom-modal metric_header"
      >
        <div class="flex space-between align-center">
          <div class="inc_hd">{clickedRow?.title}</div>
        </div>
      </ModalHeader>
      <ModalBody id="" className="incentive-table-scroll">
        <table
          class="table table-bordered incentive1 tiles-table"
          style={{
            fontWeight: "normal",
            marginBottom: "0px",
            border: "1px solid rgb(0 0 0 / 42%)",
          }}
          id="inc_table"
        >
          <thead>
            <tr>
              {headers?.map((header, index) => (
                <th
                  key={index}
                  style={{
                    textAlign: "center",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agentInsentiveInfo?.map((rowData, index) => {
              return (
                <tr key={index}>
                  {headers.map((header, colIndex) => {
                    return (
                      <td key={colIndex} style={{ textAlign: "center" }}>
                        {typeof rowData[header] === "object"
                          ? "-"
                          : rowData[header]}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <DisclaimerComponent
          currentCompanyGuid={
            currentCompanyGuid || activeOrganizationInfo?.Companyguid
          }
          organization={activeOrganizationInfo?.Organization}
          primaryProgram={activeOrganizationInfo?.Program}
          dashboard={dashboard || "L1"}
        />
      </ModalBody>
    </Modal>
  );
};
export default AgentScheduleModal;
